/**
 * CompactRatingDisplay Component
 *
 * Displays star ratings in a compact format for use in photo templates.
 * Shows different information based on user role:
 * - Photographers see the average rating from all critics
 * - Critics see their own rating for the photo (interactive - click to rate!)
 *
 * Feature: 649-critique-feedback-options (Rating display in templates)
 *
 * @module CompactRatingDisplay
 */

import { useState, useCallback } from 'react';
import { Star } from '@/components/atoms/Star/Star';
import { useCritiqueMode } from '@/contexts/CritiqueModeContext';
import {
  useRatingSummaryForPhoto,
  useMyRatingForPhoto,
  useSubmitRating,
} from '@/graphql/hooks/usePhotoRatingsGraphQL';
import logger from '@/utils/logger';

export interface CompactRatingDisplayProps {
  /** Global ID of the photo */
  photoId: string;
  /** Size of stars in pixels */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Variant for different layouts */
  variant?: 'inline' | 'overlay' | 'stacked';
}

/**
 * CompactRatingDisplay - Shows rating based on user role
 *
 * Automatically determines what to show based on CritiqueModeContext:
 * - Photographers see average rating with vote count
 * - Critics see their own rating
 *
 * @example
 * ```tsx
 * // In a template, inside CritiqueModeProvider
 * <CompactRatingDisplay photoId={data.photoData.id} size={16} variant="overlay" />
 * ```
 */
export function CompactRatingDisplay({
  photoId,
  size = 14,
  className = '',
  variant = 'inline',
}: CompactRatingDisplayProps) {
  const { sessionId, isPhotographer, hasRatingFeedback } = useCritiqueMode();

  // Skip all queries if not in critique mode or no rating feedback
  const skip = !sessionId || !hasRatingFeedback;

  // Fetch rating summary (for photographers)
  const { ratingSummary, loading: summaryLoading } = useRatingSummaryForPhoto(
    photoId,
    sessionId,
    { skip: skip || !isPhotographer }
  );

  // Fetch user's own rating (for critics)
  const { myRating, loading: myRatingLoading } = useMyRatingForPhoto(
    photoId,
    sessionId,
    { skip: skip || isPhotographer }
  );

  // Don't render if not in critique mode or no rating feedback enabled
  if (!sessionId || !hasRatingFeedback) {
    return null;
  }

  const loading = isPhotographer ? summaryLoading : myRatingLoading;

  // Loading state - show placeholder stars
  if (loading) {
    return (
      <div className={getContainerClasses(variant, className)}>
        <div className="flex gap-0.5 opacity-50">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} fill="empty" size={size} fillColor="#9ca3af" emptyColor="transparent" />
          ))}
        </div>
      </div>
    );
  }

  if (isPhotographer) {
    // Photographer view: show average rating
    return (
      <PhotographerRating
        ratingSummary={ratingSummary}
        size={size}
        variant={variant}
        className={className}
      />
    );
  }

  // Critic view: show own rating (interactive)
  return (
    <CriticRating
      photoId={photoId}
      sessionId={sessionId}
      myRating={myRating?.rating ?? null}
      size={size}
      variant={variant}
      className={className}
    />
  );
}

/**
 * Get container classes based on variant
 */
function getContainerClasses(variant: 'inline' | 'overlay' | 'stacked', className: string): string {
  const base = 'flex items-center gap-1';
  const variantClasses = {
    inline: '',
    overlay: 'absolute bottom-1 left-1 bg-black/70 rounded px-1.5 py-0.5',
    stacked: 'flex-col items-start gap-0.5',
  };
  return `${base} ${variantClasses[variant]} ${className}`.trim();
}

/**
 * Get star fill based on rating value
 */
function getStarFill(index: number, rating: number): 'full' | 'half' | 'empty' {
  const threshold = index + 1;
  if (rating >= threshold) return 'full';
  if (rating >= threshold - 0.5) return 'half';
  return 'empty';
}

/**
 * Photographer rating view - shows average with vote count
 */
interface PhotographerRatingProps {
  ratingSummary: { averageRating: number; voteCount: number } | null;
  size: number;
  variant: 'inline' | 'overlay' | 'stacked';
  className: string;
}

function PhotographerRating({ ratingSummary, size, variant, className }: PhotographerRatingProps) {
  const averageRating = ratingSummary?.averageRating ?? 0;
  const voteCount = ratingSummary?.voteCount ?? 0;

  // Photographer view uses secondary color (teal in dark mode, orange tint in light mode)
  const starColor = 'rgb(var(--color-secondary))';

  // No ratings yet
  if (voteCount === 0) {
    return (
      <div className={getContainerClasses(variant, className)}>
        <div className="flex gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} fill="empty" size={size} fillColor="#9ca3af" emptyColor="transparent" />
          ))}
        </div>
        <span className={`text-xs ${variant === 'overlay' ? 'text-gray-300' : 'text-muted'}`}>
          --
        </span>
      </div>
    );
  }

  return (
    <div
      className={getContainerClasses(variant, className)}
      role="img"
      aria-label={`Average rating: ${averageRating.toFixed(1)} out of 5 stars from ${voteCount} votes`}
    >
      <div className="flex gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            fill={getStarFill(i, averageRating)}
            size={size}
            fillColor={starColor}
            emptyColor="transparent"
          />
        ))}
      </div>
      <span className={`text-xs font-medium ${variant === 'overlay' ? 'text-white' : 'text-foreground'}`}>
        {averageRating.toFixed(1)}
      </span>
      <span className={`text-xs ${variant === 'overlay' ? 'text-gray-300' : 'text-muted'}`}>
        ({voteCount})
      </span>
    </div>
  );
}

/**
 * Critic rating view - interactive, click to rate!
 */
interface CriticRatingProps {
  photoId: string;
  sessionId: string;
  myRating: number | null;
  size: number;
  variant: 'inline' | 'overlay' | 'stacked';
  className: string;
}

function CriticRating({ photoId, sessionId, myRating, size, variant, className }: CriticRatingProps) {
  const { submitRating, loading } = useSubmitRating();
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Critic view uses primary color (orange in dark mode, teal in light mode)
  const starColor = 'rgb(var(--color-primary))';
  const emptyStarColor = '#9ca3af';

  const handleClick = useCallback(async (rating: number) => {
    if (submitting || loading) return;

    setSubmitting(true);
    try {
      await submitRating(photoId, sessionId, rating);
      logger.info('CompactRating', 'Rating submitted from template', { photoId, rating });
    } catch (error) {
      logger.error('CompactRating', 'Failed to submit rating', { error });
    } finally {
      setSubmitting(false);
    }
  }, [photoId, sessionId, submitRating, submitting, loading]);

  const isLoading = submitting || loading;
  const displayValue = hoverValue ?? myRating ?? 0;

  return (
    <div
      className={`${getContainerClasses(variant, className)} ${isLoading ? 'opacity-50' : ''}`}
      role="slider"
      aria-label="Rate this photo"
      aria-valuemin={1}
      aria-valuemax={5}
      aria-valuenow={myRating ?? undefined}
      aria-valuetext={myRating ? `${myRating} out of 5 stars` : 'Not rated'}
      onMouseLeave={() => setHoverValue(null)}
    >
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((rating) => (
          <button
            key={rating}
            type="button"
            disabled={isLoading}
            onClick={() => handleClick(rating)}
            onMouseEnter={() => setHoverValue(rating)}
            className={`
              p-0 rounded transition-transform cursor-pointer
              hover:scale-110 focus:outline-none focus:ring-1 focus:ring-primary
              disabled:cursor-default disabled:hover:scale-100
            `}
            aria-label={`Rate ${rating} star${rating !== 1 ? 's' : ''}`}
          >
            <Star
              fill={rating <= displayValue ? 'full' : 'empty'}
              size={size}
              fillColor={rating <= displayValue ? starColor : emptyStarColor}
              emptyColor="transparent"
            />
          </button>
        ))}
      </div>
      <span className={`text-xs ${variant === 'overlay' ? 'text-gray-300' : 'text-muted'}`}>
        {myRating ? `You: ${myRating}` : 'Click to rate'}
      </span>
    </div>
  );
}

CompactRatingDisplay.displayName = 'CompactRatingDisplay';
